<script>
  import { onMount, tick } from 'svelte';
  import { createInboxApi, errorText, parameterLabels, deliveryLabels } from './api.js';
  import RecoveryPanel from './RecoveryPanel.svelte';
  import TemplateVerificationPanel from './TemplateVerificationPanel.svelte';
  import ReceiptReviewPanel from './ReceiptReviewPanel.svelte';
  import DraftConflictPanel from './DraftConflictPanel.svelte';
  import InboxDeviceSetup from './InboxDeviceSetup.svelte';
  import InboxUpdate from './InboxUpdate.svelte';
  import { registerUpdateGuard, updateState, updateFrozen } from './appUpdates.js';
  import { createRecoverySession, requestDigest } from './browserRecovery.js';
  import { conversationPath, inboxLoginHref } from './navigation.js';
  import { registerNavigationGuard } from '../navigationGuard.js';
  import { compareMessages, requireHistoryContract } from './timeline.js';

  export let params = {};
  export let onRouteEvent = null;
  let observedRoute = null;
  let options = null, conversations = [], selected = null, messages = [], templates = [];
  let loading = true, loadingThread = false, error = '', needsLogin = false, online = true;
  let filter = 'all', channel = '', search = '', nextCursor = null, olderCursor = null;
  let text = '', mode = 'reply', templateId = '', parameters = {}, draftVersion = 0;
  let dirty = false, draftConflict = false, saving = false, sending = false, draftEdit = 0;
  let conflictingDraft = null, conflictLoading = false, resolvingDraft = false;
  let showContacts = false, contacts = [], contactSearch = '', timeline, newMessages = false;
  let api, alive = false, cursor = 0, selectionGeneration = 0, listGeneration = 0;
  let sessionController, sessionToken, sessionGeneration = 0;
  let checkingSession = false, sessionCheckFailed = false;
  let pollTimer, draftTimer, searchTimer, polling = false, initialized = false;
  let saveChain = Promise.resolve();
  let pendingRequests = [], preparedRequest = null, recovering = false, submissionNotice = '';
  let recovery = null, recoveryStorageIssue = '';
  let recoveryMessage = null;
  let showTemplateVerification = false;
  let showReceiptReview = false;
  let openingSnapshot = null;
  let positioningHistory = false, loadingOlder = false, olderHistoryChanged = false;
  let resyncRequired = false, resyncNeedsReload = false, resyncLatest = false;
  let readObserver;
  const readPositions = new Map();
  const readPending = new Set(), messageNodes = new Map();

  $: if (alive && initialized && observedRoute !== (params.id || '')) {
    observedRoute = params.id || '';
    if (observedRoute !== (selected?.id || '')) void openThread(observedRoute);
  }

  $: template = templates.find(item => String(item.id) === String(templateId));
  $: preview = template ? template.body.replace(/{{\s*(\w+)\s*}}/g, (_, key) => {
    const name = template.parameter_format === 'positional' ? template.parameter_names[Number(key) - 1] : key;
    return parameters[name] || `{{${name}}}`;
  }) : '';
  $: recoveryPending = !!preparedRequest || pendingRequests.some(item => item.state === 'pending');
  $: canSend = online && !checkingSession && !resyncRequired && !sending && !recovering && !loadingThread && !draftConflict && !resolvingDraft && !preparedRequest && (mode === 'note' ? !!text.trim()
    : mode === 'template' ? !!template && template.parameter_names.every(name => parameters[name]?.trim())
    : selected?.capabilities.text && !!text.trim());

  function fail(problem) {
    if (!alive || problem.name === 'AbortError') return;
    error = errorText(problem);
    if ([401, 403].includes(problem.status)) {
      sessionGeneration += 1; sessionController?.abort(); checkingSession = false; sessionCheckFailed = false;
      needsLogin = problem.status === 401;
      initialized = false; options = null; selected = null; conversations = []; messages = []; text = '';
      preparedRequest = null; pendingRequests = []; templates = []; parameters = {}; contacts = [];
      recovery = null; recoveryStorageIssue = '';
      conflictingDraft = null; conflictLoading = false; resolvingDraft = false;
      dirty = false; draftConflict = false; selectionGeneration += 1; listGeneration += 1;
      sending = false; saving = false; recovering = false; polling = false; loadingThread = false;
      readPositions.clear(); readPending.clear(); openingSnapshot = null;
      resyncRequired = false; resyncNeedsReload = false; resyncLatest = false;
      clearTimeout(pollTimer); clearTimeout(draftTimer);
    }
  }

  async function checkSession(retry = false) {
    if (!alive) return;
    const token = localStorage.getItem('jwtToken');
    if (!token || !options?.user?.id) { fail({ status: 401 }); return; }
    if (token === sessionToken && !retry) return;
    const owner = options.user.id, generation = ++sessionGeneration;
    sessionToken = token;
    checkingSession = true; sessionCheckFailed = false; initialized = false;
    selectionGeneration += 1; listGeneration += 1;
    sessionController?.abort();
    clearTimeout(pollTimer); clearTimeout(draftTimer); clearTimeout(searchTimer);
    saving = false; sending = false; recovering = false; polling = false;
    conflictLoading = false; resolvingDraft = false; loadingThread = false;
    // In-flight reads belong to the old credential generation. Only confirmed
    // acknowledgements survive; the verified session must be able to retry them.
    readPending.clear(); openingSnapshot = null; loadingOlder = false; positioningHistory = false;
    saveChain = Promise.resolve();
    sessionController = new AbortController();
    const verifiedApi = createInboxApi(sessionController.signal, () => { void checkSession(); });
    try {
      const verified = await verifiedApi('options/');
      if (!alive || generation !== sessionGeneration) return;
      // Only the authenticated server identity can authorize retaining a draft.
      // Never trust decoded JWT claims, names or browser account metadata.
      if (verified.user?.id !== owner) { fail({ status: 401 }); return; }
      requireHistoryContract(verified);
      api = verifiedApi; options = verified; needsLogin = false;
      checkingSession = false; initialized = true; loading = false; error = '';
      if (!selected) observedRoute = null;
      // Keep the old committed cursor: replacing it with verification's newer
      // cursor would skip messages received during this identity check.
      await reconcilePending();
      if (!alive || generation !== sessionGeneration) return;
      if (draftConflict) void loadDraftConflict();
      void poll();
    } catch (problem) {
      if (!alive || generation !== sessionGeneration || problem.name === 'AbortError') return;
      if ([401, 403].includes(problem.status)) fail(problem);
      else sessionCheckFailed = true; // Private state stays hidden, in memory only.
    }
  }

  function listQuery(append = false) {
    const query = new URLSearchParams({ filter, limit: '30' });
    if (channel) query.set('channel', channel);
    if (search.trim()) query.set('q', search.trim());
    if (append && nextCursor) query.set('before', nextCursor);
    return query;
  }

  async function loadList(append = false) {
    const generation = ++listGeneration, query = listQuery(append);
    const data = await api(`conversations/?${query}`);
    if (!alive || generation !== listGeneration) return;
    conversations = append ? [...new Map([...conversations, ...data.results].map(item => [item.id, item])).values()] : data.results;
    nextCursor = data.next_cursor;
  }

  async function bootstrap() {
    try {
      const data = await api('options/');
      if (!alive) return;
      requireHistoryContract(data);
      options = data; cursor = data.cursor;
      recovery = createRecoverySession(data.user.id, code => { if (alive) recoveryStorageIssue = code; });
      await loadList();
      initialized = true;
    } catch (problem) { fail(problem); }
    finally { if (alive) loading = false; }
  }

  function nearBottom() { return !timeline || timeline.scrollHeight - timeline.scrollTop - timeline.clientHeight < 40; }

  function trackMessage(node, id) {
    messageNodes.set(id, node); readObserver?.observe(node);
    return { destroy() { readObserver?.unobserve(node); if (messageNodes.get(id) === node) messageNodes.delete(id); } };
  }

  function visibleNode(node) {
    if (!timeline || !node) return false;
    const bounds = timeline.getBoundingClientRect(), rect = node.getBoundingClientRect();
    return bounds.height > 0 && rect.width > 0 && rect.height > 0
      && Math.min(bounds.bottom, window.innerHeight, rect.bottom) - Math.max(bounds.top, 0, rect.top) >= Math.min(24, rect.height);
  }

  function scrollAnchor() {
    const message = messages.find(item => visibleNode(messageNodes.get(item.id)));
    return message ? { id: message.id, offset: messageNodes.get(message.id).getBoundingClientRect().top - timeline.getBoundingClientRect().top } : null;
  }

  function restoreAnchor(anchor) {
    const node = anchor && messageNodes.get(anchor.id);
    if (timeline && node) timeline.scrollTop += node.getBoundingClientRect().top - timeline.getBoundingClientRect().top - anchor.offset;
  }

  async function markDisplayed() {
    if (!alive || !online || !selected || checkingSession || resyncRequired || loadingThread || positioningHistory || document.hidden) return;
    const ident = selected.id, generation = sessionGeneration;
    const acknowledged = readPositions.get(ident) || new Set();
    const ids = messages.filter(item => item.direction === 'inbound' && item.kind === 'message' && !item.historical
      && !acknowledged.has(item.id) && !readPending.has(item.id) && visibleNode(messageNodes.get(item.id)))
      .slice(0, 100).map(item => item.id);
    if (!ids.length) return;
    ids.forEach(id => readPending.add(id));
    try {
      const result = await api(`conversations/${ident}/read/`, { method: 'POST', body: { message_ids: ids } });
      if (!alive || generation !== sessionGeneration) return;
      if (!Array.isArray(result.message_ids) || result.message_ids.length !== ids.length || !ids.every(id => result.message_ids.includes(id))) {
        throw Object.assign(new Error('Unconfirmed read acknowledgement'), { code: 'invalid_read_confirmation' });
      }
      ids.forEach(id => acknowledged.add(id)); readPositions.set(ident, acknowledged);
    } catch (problem) {
      if (alive && generation === sessionGeneration && (selected?.id === ident || [401, 403].includes(problem.status))) fail(problem);
    } finally { if (generation === sessionGeneration) ids.forEach(id => readPending.delete(id)); }
  }

  async function scrollToLatest() {
    const ident = selected?.id;
    positioningHistory = true;
    await tick();
    if (!alive || selected?.id !== ident) return;
    if (timeline) timeline.scrollTop = timeline.scrollHeight;
    newMessages = false; positioningHistory = false;
    await markDisplayed();
  }

  async function navigateThread(ident) {
    if (sending || checkingSession) return;
    await flushDraft();
    if (dirty || draftConflict) { error = errorText({ code: 'draft_not_saved' }); return; }
    // The router owns navigation. Never render an editable target composer and
    // then change routes: that can remount it after the user has already typed.
    if (onRouteEvent) onRouteEvent({ type: 'inbox-thread', id: ident });
    else await openThread(ident); // Also usable as an embedded, non-routed panel.
  }

  async function openThread(ident) {
    if (sending || checkingSession) return;
    await flushDraft();
    if (dirty || draftConflict) { error = errorText({ code: 'draft_not_saved' }); return; }
    const generation = ++selectionGeneration;
    openingSnapshot = ident ? { id: ident, messages: new Map(), thread: null } : null;
    positioningHistory = true; loadingOlder = false; olderHistoryChanged = false;
    // Never leave another customer's history visible under a failed/changed URL.
    selected = null; messages = []; templates = []; text = ''; parameters = {};
    preparedRequest = null; pendingRequests = []; submissionNotice = '';
    loadingThread = true; error = ''; showContacts = false; recoveryMessage = null; showTemplateVerification = false;
    if (!conversationPath(ident)) {
      loadingThread = false; error = errorText({ code: 'conversation_unavailable' }); return;
    }
    if (!ident) {
      loadingThread = false;
      return;
    }
    try {
      const [thread, history, available, draft] = await Promise.all([
        api(`conversations/${ident}/`), api(`conversations/${ident}/messages/?order=chronological`),
        api(`conversations/${ident}/templates/`), api(`conversations/${ident}/draft/`),
      ]);
      if (!alive || generation !== selectionGeneration) return;
      selected = thread; messages = []; mergeMessages(history.results); olderCursor = history.older_cursor; templates = available.results;
      if (openingSnapshot?.id === ident) {
        if (openingSnapshot.thread?.revision > selected.revision) selected = openingSnapshot.thread;
        const loaded = new Set(messages.map(message => message.id));
        mergeMessages([...openingSnapshot.messages.values()].filter(message => loaded.has(message.id)));
        openingSnapshot = null;
      }
      preparedRequest = null;
      applyDraft(draft);
      draftEdit = 0; loadingThread = false; newMessages = false; submissionNotice = '';
      pendingRequests = recovery.read(ident);
      await reconcilePending();
      await scrollToLatest();
    } catch (problem) {
      if (generation === selectionGeneration) fail(problem.status === 404 ? { code: 'conversation_unavailable' } : problem);
    }
    finally { if (generation === selectionGeneration) loadingThread = false; }
  }

  function applyDraft(draft) {
    text = draft.text; mode = draft.mode; templateId = draft.template_id == null ? '' : String(draft.template_id);
    parameters = draft.parameters; draftVersion = draft.version; dirty = false; draftConflict = false;
    conflictingDraft = null;
  }

  function draftPayload() {
    return { text, mode, template_id: templateId ? Number(templateId) : null, parameters: { ...parameters } };
  }

  async function loadDraftConflict() {
    if (!selected || !draftConflict || !online || conflictLoading) return;
    const ident = selected.id, generation = selectionGeneration;
    conflictLoading = true;
    // An explicit refresh must not leave actions enabled for an older preview.
    conflictingDraft = null;
    try {
      const snapshot = await api(`conversations/${ident}/draft/`);
      if (!alive || generation !== selectionGeneration || selected?.id !== ident || !draftConflict) return;
      conflictingDraft = snapshot;
    } catch (problem) { if (generation === selectionGeneration) fail(problem); }
    finally { if (alive && generation === selectionGeneration) conflictLoading = false; }
  }

  async function resolveDraftConflict(choice) {
    if (!selected || !draftConflict || !conflictingDraft || !online || conflictLoading || resolvingDraft || saving || sending) return;
    if (choice === 'server') {
      // Adopt exactly the preview the user chose. No write or send; a later
      // remote edit will still be caught by the next expected-version check.
      applyDraft(conflictingDraft); draftEdit += 1; error = '';
      return;
    }
    if (choice !== 'local') return;
    const ident = selected.id, generation = selectionGeneration, edit = draftEdit;
    const body = { ...draftPayload(), expected_version: conflictingDraft.version };
    resolvingDraft = true; error = ''; clearTimeout(draftTimer);
    try {
      const saved = await api(`conversations/${ident}/draft/`, { method: 'PUT', body });
      if (!alive || generation !== selectionGeneration || selected?.id !== ident) return;
      draftVersion = saved.version; draftConflict = false; conflictingDraft = null;
      dirty = draftEdit !== edit;
    } catch (problem) {
      if (generation !== selectionGeneration) return;
      fail(problem);
      // Never retry a changed revision automatically. Show it for a new choice.
      if (problem.code === 'draft_conflict') await loadDraftConflict();
    } finally { if (alive && generation === selectionGeneration) resolvingDraft = false; }
  }

  async function reconcilePending() {
    if (!selected || checkingSession || !online || recovering || sending || !options) return;
    const ident = selected.id, generation = selectionGeneration, edit = draftEdit;
    recovering = true;
    try {
      const server = await api(`conversations/${ident}/requests/`);
      if (!alive || generation !== selectionGeneration || selected?.id !== ident) return;
      preparedRequest = server.results[0] || null;
      pendingRequests = recovery.read(ident);
      for (const saved of pendingRequests.filter(item => item.state === 'pending')) {
        const result = await api(`conversations/${ident}/reconcile/`, { method: 'POST', body: { idempotency_key: saved.key } });
        if (!alive || generation !== selectionGeneration || selected?.id !== ident) return;
        if (result.found && result.message?.id && result.message.conversation_id === ident) {
          mergeMessages([result.message]);
          if (!dirty && draftEdit === edit && draftVersion === saved.draftVersion) {
            const latest = await api(`conversations/${ident}/draft/`);
            if (!alive || generation !== selectionGeneration || selected?.id !== ident) return;
            if (!dirty && draftEdit === edit) applyDraft(latest);
          }
          recovery.forget(saved.key);
          if (preparedRequest?.key === saved.key) preparedRequest = null;
          submissionNotice = 'Ankstesnė užklausa rasta. Nauja žinutė nebuvo siunčiama; pristatymo būseną matysite pokalbyje.';
        } else if (result.found === false && result.request?.conversation_id === ident) {
          if (result.request.state === 'prepared') preparedRequest = result.request;
          else if (['cancelled', 'expired'].includes(result.request.state)) {
            recovery.forget(saved.key);
            if (preparedRequest?.key === saved.key) preparedRequest = null;
          }
        } else if (result.found !== false) {
          throw Object.assign(new Error('Invalid reconciliation result'), { code: 'send_outcome_unknown' });
        }
      }
      pendingRequests = recovery.read(ident);
    } catch (problem) { fail(problem); }
    finally { if (alive && generation === selectionGeneration) recovering = false; }
  }

  function edited() {
    dirty = true; draftEdit += 1;
    clearTimeout(draftTimer);
    draftTimer = setTimeout(() => { void flushDraft(); }, 500);
  }

  function flushDraft() {
    clearTimeout(draftTimer);
    if (!selected || checkingSession || !dirty || !online || draftConflict || resolvingDraft) return saveChain;
    const ident = selected.id, edit = draftEdit, generation = sessionGeneration, request = api;
    const payload = draftPayload();
    saveChain = saveChain.then(async () => {
      if (!alive || generation !== sessionGeneration || selected?.id !== ident || draftConflict) return;
      saving = true;
      try {
        const saved = await request(`conversations/${ident}/draft/`, { method: 'PUT', body: { ...payload, expected_version: draftVersion } });
        if (!alive || generation !== sessionGeneration || selected?.id !== ident) return;
        draftVersion = saved.version;
        if (edit === draftEdit) dirty = false;
      } catch (problem) {
        if (generation !== sessionGeneration) return;
        if (problem.code === 'draft_conflict') { draftConflict = true; void loadDraftConflict(); }
        fail(problem);
      } finally { if (alive && generation === sessionGeneration) saving = false; }
    });
    return saveChain;
  }

  function mergeMessages(items) {
    const merged = new Map(messages.map(message => [message.id, message]));
    for (const item of items) {
      const previous = merged.get(item.id);
      if (!previous || item.revision >= previous.revision) merged.set(item.id, item);
    }
    messages = [...merged.values()].sort(compareMessages);
  }

  async function send() {
    if (checkingSession || (!preparedRequest && !canSend) || !selected || !online || sending || recovering || loadingThread) return;
    sending = true; error = '';
    const ident = selected.id, edit = draftEdit, generation = sessionGeneration, request = api;
    const savedCommand = preparedRequest;
    const endpoint = savedCommand?.endpoint || (mode === 'note' ? 'notes' : mode === 'template' ? 'templates' : 'messages');
    const payload = savedCommand?.payload || (mode === 'template' ? { template_id: template.id, parameters: { ...parameters }, expected_template_hash: template.definition_hash } : { text });
    let attempt = null, stage = 'local';
    try {
      if (!savedCommand) await flushDraft();
      if (!alive || generation !== sessionGeneration || selected?.id !== ident || draftEdit !== edit) return;
      if (!savedCommand && (dirty || draftConflict)) { error = errorText({ code: 'draft_not_saved' }); return; }
      const digest = await requestDigest(ident, endpoint, payload);
      if (!alive || generation !== sessionGeneration || selected?.id !== ident) return;
      const previous = recovery.read(ident);
      if (previous.some(item => item.state === 'pending' && item.digest !== digest)) {
        throw Object.assign(new Error('An earlier request is unresolved'), { code: 'send_reconciliation_required' });
      }
      for (const obsolete of previous.filter(item => item.state === 'rejected' && item.digest !== digest)) recovery.forget(obsolete.key);
      attempt = savedCommand ? { key: savedCommand.key, thread: ident, endpoint, digest,
        draftVersion: savedCommand.draft_version, createdAt: Date.now() } : previous.find(item => item.digest === digest) || { key: crypto.randomUUID(), thread: ident,
        endpoint, digest, draftVersion, createdAt: Date.now() };
      if (attempt.state === 'rejected') attempt = { ...attempt, draftVersion };
      attempt = recovery.remember({ ...attempt, state: 'pending' }, { serverOwned: !!savedCommand });
      pendingRequests = recovery.read(ident);
      const body = { ...payload, idempotency_key: attempt.key, submitted_draft_version: attempt.draftVersion };
      if (!savedCommand) {
        stage = 'prepare';
        const command = await request(`conversations/${ident}/requests/`, { method: 'POST', body: {
          idempotency_key: attempt.key, endpoint, payload, submitted_draft_version: attempt.draftVersion,
        } });
        if (!alive || generation !== sessionGeneration || selected?.id !== ident) return;
        if (command.state !== 'prepared' || command.key !== attempt.key || command.conversation_id !== ident) {
          // A replay may be terminal. Reconcile it without issuing another send.
          sending = false; await reconcilePending(); return;
        }
        preparedRequest = command;
      }
      stage = 'submit';
      const result = await request(`conversations/${ident}/${endpoint}/`, { method: 'POST', body });
      if (!alive || generation !== sessionGeneration) return;
      if (!result.id || result.conversation_id !== ident) throw Object.assign(new Error('Invalid saved result'), { code: 'send_outcome_unknown' });
      stage = 'accepted';
      if (!alive) return;
      if (selected?.id === ident) {
        mergeMessages([result]);
        preparedRequest = null;
        if (draftEdit === edit && result.draft_cleared_version === draftVersion + 1) {
          text = ''; parameters = {}; dirty = false; draftVersion = result.draft_cleared_version; draftEdit += 1;
        } else if (draftEdit === edit && !dirty) {
          const latest = await request(`conversations/${ident}/draft/`);
          if (alive && generation === sessionGeneration && selected?.id === ident && draftEdit === edit && !dirty) applyDraft(latest);
        }
        recovery.forget(attempt.key);
        pendingRequests = recovery.read(ident); submissionNotice = '';
        await scrollToLatest();
      }
      if (generation === sessionGeneration) await loadList();
    } catch (problem) {
      if (generation !== sessionGeneration || problem.name === 'AbortError') return;
      if (attempt && stage === 'prepare' && problem.status === 400) {
        try { recovery.remember({ ...attempt, state: 'rejected' }); pendingRequests = recovery.read(ident); }
        catch (storageError) { fail(storageError); return; }
      }
      fail(['prepare', 'submit'].includes(stage) && !problem.status ? { ...problem, code: 'send_outcome_unknown' } : problem);
    }
    finally { if (alive && generation === sessionGeneration) sending = false; }
  }

  async function cancelPrepared() {
    return cancelPendingKey(preparedRequest);
  }

  async function cancelPendingKey(saved) {
    if (!saved || checkingSession || sending || recovering || !online) return;
    const ident = selected.id, generation = sessionGeneration;
    recovering = true; error = '';
    // Retain the exact key in memory if the cancellation response is lost.
    recovery.track({ key: saved.key, thread: ident, draftVersion: saved.draft_version ?? saved.draftVersion, state: 'pending' });
    try {
      const result = await api(`conversations/${ident}/requests/cancel/`, { method: 'POST', body: { idempotency_key: saved.key } });
      if (!alive || generation !== sessionGeneration || selected?.id !== ident) return;
      if (result.key !== saved.key || result.conversation_id !== ident || !['committed', 'cancelled', 'expired'].includes(result.state)) throw new Error('Invalid command outcome');
      if (result.message) {
        mergeMessages([result.message]);
        if (!dirty && result.draft_cleared_version === draftVersion + 1) {
          text = ''; parameters = {}; draftVersion = result.draft_cleared_version; draftEdit += 1;
        }
      }
      recovery.forget(saved.key);
      pendingRequests = recovery.read(ident);
      if (preparedRequest?.key === saved.key) preparedRequest = null;
      submissionNotice = result.state === 'committed' ? 'Užklausa jau priimta. Atšaukimas jos nesustabdė; patikrinkite siuntimo būseną.' : 'Paruošta užklausa atšaukta. Juodraštis nepakeistas.';
    } catch (problem) { fail(problem); }
    finally { if (alive && generation === sessionGeneration) recovering = false; }
  }

  async function updateThread(update) {
    const ident = selected.id;
    try {
      const thread = await api(`conversations/${ident}/`, { method: 'PATCH', body: { ...update, expected_version: selected.version } });
      if (alive && selected?.id === ident) selected = thread;
      await loadList();
    } catch (problem) {
      if (problem.code === 'conversation_conflict') {
        try { const thread = await api(`conversations/${ident}/`); if (alive && selected?.id === ident) selected = thread; } catch { /* Keep the original conflict visible. */ }
      }
      fail(problem);
    }
  }

  async function olderMessages() {
    if (!olderCursor || !selected || loadingOlder || resyncRequired) return;
    const ident = selected.id, generation = selectionGeneration;
    loadingOlder = true;
    openingSnapshot = { id: ident, messages: new Map(), thread: null };
    try {
      const query = new URLSearchParams({ order: 'chronological', cursor: olderCursor });
      const data = await api(`conversations/${ident}/messages/?${query}`);
      if (!alive || generation !== selectionGeneration || selected?.id !== ident) return;
      const anchor = scrollAnchor(); positioningHistory = true;
      mergeMessages(data.results); olderCursor = data.older_cursor;
      if (openingSnapshot?.id === ident) {
        if (openingSnapshot.thread?.revision > selected.revision) selected = openingSnapshot.thread;
        const loaded = new Set(messages.map(message => message.id));
        mergeMessages([...openingSnapshot.messages.values()].filter(message => loaded.has(message.id)));
        openingSnapshot = null;
      }
      if (!olderCursor) olderHistoryChanged = false;
      await tick();
      if (!alive || generation !== selectionGeneration) return;
      restoreAnchor(anchor); positioningHistory = false; await markDisplayed();
    } catch (problem) { if (generation === selectionGeneration) fail(problem); }
    finally { if (generation === selectionGeneration) { loadingOlder = false; positioningHistory = false; openingSnapshot = null; } }
  }

  async function resynchronize() {
    const generation = sessionGeneration, selection = selectionGeneration, listVersion = ++listGeneration;
    const ident = selected?.id, oldest = resyncLatest ? null : messages[0], query = listQuery();
    try {
      // Capture the committed cursor BEFORE snapshots, but do not promote it
      // until the entire replacement view succeeds. Failed retries stay resyncs.
      const start = await api('options/');
      requireHistoryContract(start);
      const [list, thread, history] = await Promise.all([
        api(`conversations/?${query}`),
        ident ? api(`conversations/${ident}/`) : null,
        ident ? api(`conversations/${ident}/messages/?order=chronological`) : null,
      ]);
      const snapshot = history ? [...history.results] : [];
      let older = history?.older_cursor, pages = 1;
      const positions = new Set();
      while (older && oldest && (!snapshot.length || compareMessages(snapshot[0], oldest) > 0)) {
        // Never turn resync into an unbounded download or follow a cursor cycle.
        if (pages >= 20 || positions.has(older)) throw Object.assign(new Error('History range requires an explicit reload'), { code: 'history_range_reload_required' });
        positions.add(older);
        if (!alive || generation !== sessionGeneration || selection !== selectionGeneration) return false;
        const page = await api(`conversations/${ident}/messages/?${new URLSearchParams({ order: 'chronological', cursor: older })}`);
        snapshot.unshift(...page.results); older = page.older_cursor; pages += 1;
      }
      if (!alive || generation !== sessionGeneration || selection !== selectionGeneration || listVersion !== listGeneration) return false;
      const anchor = scrollAnchor(); positioningHistory = true;
      conversations = list.results; nextCursor = list.next_cursor;
      if (ident) { selected = thread; messages = []; mergeMessages(snapshot); olderCursor = older; }
      options = start; cursor = start.cursor; olderHistoryChanged = false;
      await tick();
      if (!alive || generation !== sessionGeneration || selection !== selectionGeneration) return false;
      positioningHistory = false; resyncRequired = false; resyncNeedsReload = false;
      if (resyncLatest) { resyncLatest = false; await scrollToLatest(); }
      else { restoreAnchor(anchor); await markDisplayed(); }
      return true;
    } catch (problem) {
      if (alive && generation === sessionGeneration) {
        if (problem.code === 'history_range_reload_required') resyncNeedsReload = true;
        fail(problem);
      }
      return false;
    }
  }

  async function poll() {
    clearTimeout(pollTimer);
    if (!alive || polling || !initialized || !online) return;
    if (document.hidden) { pollTimer = setTimeout(poll, 15000); return; }
    polling = true;
    const generation = sessionGeneration;
    let delay = 3000;
    try {
      if (resyncRequired) {
        if (resyncNeedsReload) { delay = 15000; return; }
        if (loadingThread || loadingOlder) { delay = 250; return; }
        delay = await resynchronize() ? 0 : 15000; return;
      }
      const data = await api(`changes/?since=${cursor}&limit=200`);
      if (!alive || generation !== sessionGeneration) return;
      if ((loadingThread || loadingOlder) && openingSnapshot) {
        const changed = data.conversations.find(item => item.id === openingSnapshot.id);
        if (changed && (!openingSnapshot.thread || changed.revision >= openingSnapshot.thread.revision)) openingSnapshot.thread = changed;
        for (const item of data.messages.filter(item => item.conversation_id === openingSnapshot.id)) {
          const previous = openingSnapshot.messages.get(item.id);
          if (!previous || item.revision >= previous.revision) openingSnapshot.messages.set(item.id, item);
        }
        // Keep a bounded overlay, and do NOT advance the committed cursor. After
        // loading, normal polling replays all changes, including off-page records.
        while (openingSnapshot.messages.size > 200) openingSnapshot.messages.delete(openingSnapshot.messages.keys().next().value);
        return;
      }
      const atBottom = nearBottom();
      if (selected) {
        const changed = data.conversations.find(item => item.id === selected.id);
        if (changed && changed.revision >= selected.revision) selected = changed;
        const loaded = new Set(messages.map(item => item.id));
        const candidates = data.messages.filter(item => item.conversation_id === selected.id);
        const updates = candidates.filter(item => loaded.has(item.id) || !olderCursor || (messages[0] && compareMessages(item, messages[0]) >= 0));
        if (updates.length !== candidates.length) olderHistoryChanged = true;
        if (updates.length) {
          const ident = selected.id, anchor = scrollAnchor(), latest = messages.at(-1);
          const freshTail = updates.some(item => !loaded.has(item.id) && !item.historical && (!latest || compareMessages(item, latest) > 0));
          positioningHistory = true;
          mergeMessages(updates);
          if (atBottom && freshTail) await scrollToLatest();
          else {
            await tick();
            if (!alive || generation !== sessionGeneration || selected?.id !== ident) return;
            restoreAnchor(anchor); positioningHistory = false;
            if (freshTail) newMessages = true;
          }
        }
      }
      if (!alive || generation !== sessionGeneration) return;
      cursor = data.cursor;
      if (data.conversations.length) await loadList();
      await markDisplayed();
      if (data.has_more) delay = 0;
    } catch (problem) {
      if (generation !== sessionGeneration) return;
      if (problem.code === 'resync_required') {
        resyncRequired = true;
        delay = loadingThread || loadingOlder ? 250 : await resynchronize() ? 0 : 15000;
      } else { fail(problem); delay = 15000; }
    } finally {
      if (generation === sessionGeneration) {
        polling = false;
        if (alive && initialized) pollTimer = setTimeout(poll, delay);
      }
    }
  }

  async function findContacts() {
    try { const data = await api(`contacts/?q=${encodeURIComponent(contactSearch)}`); if (alive) contacts = data.results; }
    catch (problem) { fail(problem); }
  }

  async function startContact(contact) {
    try {
      const thread = await api('conversations/', { method: 'POST', body: { contact_id: contact.id } });
      if (!alive) return;
      await loadList(); await navigateThread(thread.id);
    } catch (problem) { fail(problem); }
  }

  function refreshList() { loadList().catch(fail); }
  function searchChanged() { clearTimeout(searchTimer); searchTimer = setTimeout(refreshList, 250); }
  function formatTime(stamp) { return stamp ? new Intl.DateTimeFormat('lt-LT', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Europe/Vilnius' }).format(new Date(stamp)) : ''; }

  function messageAuthor(message) {
    if (message.kind === 'note') return 'Privati pastaba';
    if (message.direction === 'inbound') return selected.name;
    if (message.provenance?.source === 'legacy') {
      switch (message.provenance.origin) {
        case 'api': return 'Importuotas Tutory pranešimas';
        case 'business_app': return 'Importuota iš WhatsApp Business programėlės';
        case 'agent': return 'Importuotas konsultanto atsakymas';
        default: return 'Importuota žinutė — autorius nežinomas';
      }
    }
    return message.author?.name || 'Tutory pranešimas';
  }

  function messageBody(message) {
    if (message.content_purged) return 'Žinutės turinys pašalintas pagal saugojimo taisykles.';
    if (message.text) return message.text;
    if (message.provenance?.source === 'legacy') {
      if (message.content_type === 'template') return 'Istorinio šablono tekstas neišsaugotas';
      if (message.content_type === 'text') return 'Istorinės žinutės tekstas neišsaugotas';
    }
    return 'Priedas — peržiūra dar nepasiekiama';
  }

  onMount(() => {
    alive = true; online = navigator.onLine;
    readObserver = new IntersectionObserver(() => { void markDisplayed(); }, { threshold: [0, 0.1, 0.5, 1] });
    sessionController = new AbortController();
    sessionToken = localStorage.getItem('jwtToken');
    api = createInboxApi(sessionController.signal, () => { void checkSession(); });
    needsLogin = !localStorage.getItem('jwtToken');
    if (needsLogin) loading = false;
    else bootstrap().then(() => { if (alive && initialized) pollTimer = setTimeout(poll, 3000); });
    const offline = () => { online = false; clearTimeout(pollTimer); };
    const reconnect = () => { online = true; void flushDraft(); void reconcilePending(); void poll(); };
    const focus = () => { if (localStorage.getItem('jwtToken') !== sessionToken) void checkSession(); else { void poll(); void markDisplayed(); } };
    const visibility = () => { if (!document.hidden) focus(); };
    const beforeUnload = event => { if (dirty || sending) { event.preventDefault(); event.returnValue = ''; } };
    const removeNavigationGuard = registerNavigationGuard(() => {
      if (updateFrozen()) return false;
      if (dirty || draftConflict || sending) {
        error = errorText({ code: 'draft_not_saved' });
        return false;
      }
      return true;
    });
    const removeUpdateGuard = registerUpdateGuard(() => {
      const pending = recovery?.read().some(item => item.state === 'pending');
      return alive && initialized && online && !!options && !needsLogin && !loading && !loadingThread &&
        !checkingSession && sessionToken === localStorage.getItem('jwtToken') &&
        !dirty && !draftConflict && !saving && !sending && !recovering && !resolvingDraft &&
        !preparedRequest && !pending && !recoveryStorageIssue &&
        !pendingRequests.some(item => item.state === 'pending') &&
        !showReceiptReview && !showTemplateVerification && !recoveryMessage;
    });
    const logout = () => { fail({ status: 401 }); };
    const storage = event => { if (event.key === 'jwtToken' || event.key === null) {
      // A removal is an explicit logout boundary, even if a quick subsequent
      // login has already placed another token in shared storage.
      if (event.key === null || event.newValue === null) logout(); else void checkSession();
    } };
    window.addEventListener('offline', offline); window.addEventListener('online', reconnect);
    window.addEventListener('focus', focus); document.addEventListener('visibilitychange', visibility);
    window.addEventListener('beforeunload', beforeUnload);
    window.addEventListener('storage', storage); window.addEventListener('tutory:logout', logout);
    return () => {
      alive = false; readObserver?.disconnect(); messageNodes.clear(); sessionController.abort(); clearTimeout(pollTimer); clearTimeout(draftTimer); clearTimeout(searchTimer);
      window.removeEventListener('offline', offline); window.removeEventListener('online', reconnect);
      window.removeEventListener('focus', focus); document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('beforeunload', beforeUnload);
      removeNavigationGuard();
      removeUpdateGuard();
      window.removeEventListener('storage', storage); window.removeEventListener('tutory:logout', logout);
    };
  });
</script>

<InboxUpdate />
<div class="app-container inbox-page" inert={$updateState.frozen} class:review-open={showReceiptReview && options?.role === 'manager'}>
  <header class="topbar">
    <a class="brand" href="#/login-greeting" aria-label="Tutory pradžia" on:click={event => { if (dirty || sending) { event.preventDefault(); error = errorText({ code: 'draft_not_saved' }); } }}>tutory<span>.</span></a>
    <span class="workspace">Bendra pašto dėžutė</span>
    {#if options && !checkingSession}<span class="staff">{options.user.name}</span>{/if}
  </header>
  {#if error}<div class="notice error" role="alert">{error}</div>{/if}
  {#if !online}<div class="notice offline" role="status">Nėra interneto ryšio. Žinutės nebus siunčiamos.</div>{/if}
  {#if resyncRequired && !checkingSession}<div class="notice" role="status" aria-label="Pokalbio atnaujinimas">
    Atkuriama pokalbių istorija. Juodraštis nebus siunčiamas, o žinutės nebus pažymėtos perskaitytomis, kol duomenys neatnaujinti.
    {#if resyncNeedsReload}<button type="button" disabled={polling} on:click={() => { resyncNeedsReload = false; resyncLatest = true; void poll(); }}>Atverti naujausią istoriją</button>{/if}
  </div>{/if}
  {#if checkingSession}
    <section class="empty" role="status" aria-label="Sesijos patikra">
      {#if sessionCheckFailed}<p>Nepavyko patikrinti prisijungimo. Juodraštis liko šiame lange ir nebus siunčiamas.</p>
        <button type="button" on:click={() => { void checkSession(true); }}>Pakartoti sesijos patikrą</button>
      {:else}<p>Tikrinama prisijungimo sesija…</p>{/if}
    </section>
  {:else if needsLogin}
    <section class="empty"><h1>Darbuotojų pašto dėžutė</h1><p>Prisijunkite savo asmenine Tutory paskyra.</p><a href={inboxLoginHref(params.id)}>Prisijungti</a></section>
  {:else if loading}
    <p class="empty" role="status">Kraunami pokalbiai…</p>
  {:else if options}
    <InboxDeviceSetup {api} ownerId={options.user.id} on:denied={event => fail(event.detail)} />
    {#if options.role === 'manager'}
      <div class="manager-tools"><button type="button" on:click={() => { showReceiptReview = !showReceiptReview; }}>Gautų įvykių patikra</button></div>
      {#if showReceiptReview}<ReceiptReviewPanel {api} on:close={() => { showReceiptReview = false; }}
        on:changed={() => { void poll(); }} on:denied={event => fail(event.detail)} />{/if}
    {/if}
    <div class="inbox-layout" class:thread-open={!!selected}>
      <aside class="list-panel" aria-label="Pokalbių sąrašas">
        <div class="list-title"><h1>Pokalbiai</h1><button class="primary compact" on:click={() => { showContacts = !showContacts; if (showContacts) findContacts(); }}>Naujas pokalbis</button></div>
        <label class="search">Ieškoti pokalbių<input type="search" bind:value={search} on:input={searchChanged} placeholder="Vardas, numeris arba žinutė" maxlength="200" /></label>
        <div class="filters">
          <label>Pokalbiai<select aria-label="Pokalbių filtras" bind:value={filter} on:change={refreshList}>
            <option value="all">Visi</option><option value="mine">Mano</option><option value="unassigned">Nepriskirti</option>
            <option value="unread">Neperskaityti</option><option value="open">Atviri</option><option value="resolved">Išspręsti</option>
          </select></label>
          <label>Kanalas<select bind:value={channel} on:change={refreshList}><option value="">Visi kanalai</option><option value="whatsapp">WhatsApp</option><option value="viber">Viber</option></select></label>
        </div>
        {#if showContacts}
          <section class="contact-picker" aria-label="Patvirtinti kontaktai">
            <h2>Patvirtinti kontaktai</h2>
            <label>Ieškoti kontakto<input bind:value={contactSearch} on:input={findContacts} maxlength="200" /></label>
            {#each contacts as contact (contact.id)}
              <button class="contact" disabled={!contact.notifications_allowed} aria-label={`Pradėti pokalbį su ${contact.name} · ${contact.account.channel === 'whatsapp' ? 'WhatsApp' : 'Viber'}`} on:click={() => startContact(contact)}>
                <strong>{contact.name}</strong><span>{contact.account.channel === 'whatsapp' ? 'WhatsApp' : 'Viber'} · {contact.remote_id}</span>
              </button>
            {:else}<p>Kontaktų nėra. Vadovas turi patvirtinti gavėją ir sutikimą.</p>{/each}
          </section>
        {/if}
        <div class="threads">
          {#each conversations as conversation (conversation.id)}
            <button class="thread" class:active={selected?.id === conversation.id} aria-label={`Atverti pokalbį su ${conversation.name} · ${conversation.account.channel === 'whatsapp' ? 'WhatsApp' : 'Viber'}`} on:click={() => navigateThread(conversation.id)}>
              <span class="avatar">{conversation.name.slice(0, 1).toUpperCase()}</span>
              <span class="thread-summary"><span class="thread-heading"><strong>{conversation.name}</strong><small>{conversation.account.channel === 'whatsapp' ? 'WA' : 'Viber'}</small></span>
                <span class="excerpt">{conversation.last_content_purged ? 'Turinys pašalintas' : conversation.last_text || 'Dar nėra žinučių'}</span><small>{formatTime(conversation.last_message_at)}</small></span>
              {#if conversation.unread_count}<span class="unread" aria-label={`${conversation.unread_count} neperskaitytos`}>{conversation.unread_count}</span>{/if}
            </button>
          {:else}<p class="no-results">Pokalbių nerasta.</p>{/each}
          {#if nextCursor}<button class="load-more" on:click={() => loadList(true).catch(fail)}>Daugiau pokalbių</button>{/if}
        </div>
      </aside>
      <section class="conversation-panel" aria-label="Pasirinktas pokalbis">
        {#if loadingThread}<p class="empty" role="status">Kraunamas pokalbis…</p>
        {:else if selected}
          <header class="conversation-header">
            <button class="back" aria-label="Grįžti į pokalbius" disabled={sending} on:click={() => navigateThread(null)}>←</button>
            <div><h2>{selected.name}</h2><p>{selected.account.channel === 'whatsapp' ? 'WhatsApp' : 'Viber'} · {selected.remote_id}</p></div>
            <button class="resolve" on:click={() => updateThread({ status: selected.status === 'resolved' ? 'open' : 'resolved' })}>{selected.status === 'resolved' ? 'Atverti iš naujo' : 'Pažymėti išspręstu'}</button>
          </header>
          <div class="assignment"><label>Atsakingas<select value={selected.assignee?.id || ''} on:change={event => updateThread({ assignee_id: event.currentTarget.value ? Number(event.currentTarget.value) : null })}>
            <option value="">Nepriskirtas</option>{#each options.members as member}<option value={member.id}>{member.name}</option>{/each}
          </select></label><span>{selected.status === 'resolved' ? 'Išspręstas' : 'Atviras'} · Matomas visai komandai</span></div>
          {#if selected.submission_hold}
            <div class="notice" role="status" aria-label="Siuntimo eilė">
              Vėlesnės šio pokalbio žinutės laukia ankstesnio siuntimo patikros. Nauji atsakymai bus išsaugoti eilėje.
              {#if options.role === 'manager'}
                <button type="button" on:click={() => { recoveryMessage = selected.submission_hold.message_id; }}>Tikrinti eilę</button>
              {:else}Kreipkitės į vadovą. Privačias pastabas galite rašyti toliau.{/if}
            </div>
          {/if}
          <!-- Scrollable history is focusable so keyboard users can read older messages. -->
          <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
          <div class="timeline" role="log" aria-label="Pokalbio žinutės" aria-live="polite" tabindex="0" bind:this={timeline} on:scroll={markDisplayed}>
            {#if olderHistoryChanged}<p role="status" aria-label="Istorijos pakeitimai">Ankstesnė istorija pasikeitė. Atverkite ankstesnes žinutes.</p>{/if}
            {#if olderCursor}<button class="load-more" disabled={loadingOlder || resyncRequired} on:click={olderMessages}>Ankstesnės žinutės</button>{/if}
            {#each messages as message (message.id)}
              <article class="message" use:trackMessage={message.id} class:outbound={message.direction === 'outbound'} class:note={message.kind === 'note'}>
                <div class="message-meta">{messageAuthor(message)}</div>
                <p>{messageBody(message)}</p>
                <footer><time datetime={message.occurred_at}>{formatTime(message.occurred_at)}</time>{#if message.historical}<span>Istorinis įrašas</span>{/if}<span>{deliveryLabels[message.delivery_status] || message.delivery_status}</span></footer>
                {#if options.role === 'manager' && message.direction === 'outbound' && message.recovery_available === true}
                  <button type="button" on:click={() => { recoveryMessage = message.id; }}>Patikrinti siuntimą</button>
                {/if}
              </article>
            {:else}<p class="no-results">Pokalbis dar nepradėtas. Pasirinkite patvirtintą šabloną.</p>{/each}
          </div>
          {#if newMessages}<button class="new-messages" on:click={scrollToLatest}>Naujos žinutės ↓</button>{/if}
          {#if recoveryMessage && options.role === 'manager'}
            {#key recoveryMessage}<RecoveryPanel messageId={recoveryMessage} {api}
              on:close={() => { recoveryMessage = null; }} on:changed={() => { void poll(); }}
              on:denied={event => fail(event.detail)} />{/key}
          {/if}
          {#if showTemplateVerification && options.role === 'manager'}
            {#key selected.id}<TemplateVerificationPanel conversationId={selected.id} {api}
              on:close={() => { showTemplateVerification = false; }} on:changed={event => { templates = event.detail; }}
              on:denied={event => fail(event.detail)} />{/key}
          {/if}
          <form class="composer" class:note-mode={mode === 'note'} on:submit|preventDefault={send}>
            {#if draftConflict}<DraftConflictPanel localDraft={{ text, mode, template_id: templateId ? Number(templateId) : null, parameters }} serverDraft={conflictingDraft}
              loading={conflictLoading} busy={resolvingDraft || saving} {online}
              on:choose={event => resolveDraftConflict(event.detail)} on:refresh={loadDraftConflict} />{/if}
            {#if recoveryStorageIssue}<p class="policy" role="status" aria-label="Atkūrimo saugykla">
              {errorText({ code: recoveryStorageIssue })} Serveryje paruoštą užklausą vis tiek galite patikrinti, siųsti arba atšaukti žemiau. Naujam siuntimui būtina veikianti atkūrimo saugykla.
            </p>{/if}
            {#if preparedRequest}<section class="preview" aria-label="Paruošta užklausa">
              <p>{preparedRequest.endpoint === 'notes' ? 'Paruošta privati pastaba' : 'Paruošta žinutė klientui'}</p>
              <p>{preparedRequest.preview_text}</p>
              <button type="button" disabled={!online || sending || recovering} on:click={send}>Siųsti paruoštą užklausą</button>
              <button type="button" disabled={!online || sending || recovering} on:click={cancelPrepared}>Atšaukti paruoštą užklausą</button>
            </section>{/if}
            {#if recoveryPending}<p class="policy" role="status">Ankstesnės užklausos rezultatas dar nepatvirtintas. Galite tikrinti būseną arba kartoti tą pačią užklausą, nekurkite naujos kopijos.</p>
              <button type="button" disabled={!online || sending || recovering} on:click={reconcilePending}>Tikrinti ankstesnę užklausą</button>
              {#each pendingRequests.filter(item => item.state === 'pending' && item.key !== preparedRequest?.key) as unresolved (unresolved.key)}
                <button type="button" disabled={!online || sending || recovering} on:click={() => cancelPendingKey(unresolved)}>Atšaukti nepatvirtintą užklausą</button>
              {/each}
            {:else if submissionNotice}<p class="policy" role="status">{submissionNotice}</p>{/if}
            <div class="composer-toolbar"><label>Rašymo režimas<select bind:value={mode} on:change={edited} disabled={sending || resolvingDraft || recoveryPending}>
              <option value="reply">Atsakymas klientui</option><option value="note">Privati pastaba</option><option value="template">Patvirtintas šablonas</option>
            </select></label><small>{saving ? 'Saugoma…' : dirty ? 'Neišsaugoti pakeitimai' : 'Asmeninis juodraštis'}</small></div>
            {#if mode === 'template'}
              {#if options.role === 'manager'}<button type="button" on:click={() => { showTemplateVerification = !showTemplateVerification; }}>Šablonų patikra</button>{/if}
              <label>Šablonas<select bind:value={templateId} on:change={() => { parameters = {}; edited(); }} disabled={sending || resolvingDraft || recoveryPending}><option value="">Pasirinkite šabloną</option>
                {#each templates as item}<option value={String(item.id)}>{item.key} · {item.language}</option>{/each}
              </select></label>
              {#if template}<div class="template-fields">{#each template.parameter_names as name}<label>{parameterLabels[name] || name}<input value={parameters[name] || ''} maxlength="1024" disabled={sending || resolvingDraft || recoveryPending} on:input={event => { parameters = { ...parameters, [name]: event.currentTarget.value }; edited(); }} /></label>{/each}</div>
                <div class="preview" aria-label="Šablono peržiūra">{preview}</div>{/if}
            {:else}
              {#if mode === 'reply' && !selected.capabilities.text}<p class="policy">{errorText({ code: selected.capabilities.reason })}</p>{/if}
              {#if mode === 'note'}<p class="policy">Šią pastabą matys tik Tutory darbuotojai.</p>{/if}
              <label class="message-label">Žinutė<textarea bind:value={text} on:input={edited} maxlength={mode === 'note' ? 10000 : selected.capabilities.text_limit} disabled={sending || resolvingDraft} readonly={recoveryPending} rows="3" placeholder={mode === 'note' ? 'Vidinė pastaba komandai…' : 'Parašykite atsakymą…'}></textarea></label>
            {/if}
            <div class="composer-footer"><small>{mode === 'note' ? 'Nesiunčiama klientui' : 'Siuntimo būseną matysite pokalbyje'}</small><button type="submit" class="primary" disabled={!canSend}>{sending ? 'Siunčiama…' : mode === 'note' ? 'Pridėti pastabą' : mode === 'template' ? 'Siųsti šabloną' : 'Siųsti'}</button></div>
          </form>
        {:else}<div class="empty"><span class="empty-icon">✉</span><h2>Visa komanda. Viena vieta.</h2><p>Pasirinkite pokalbį arba pradėkite naują su patvirtintu kontaktu.</p><p class="subtle">Klientai ir toliau rašo per WhatsApp ir Viber.</p></div>{/if}
      </section>
    </div>
  {/if}
</div>

<style>
  .manager-tools{padding:6px 18px;border-bottom:1px solid #e8e5e1;flex-shrink:0}.manager-tools button{font-size:11px;padding:5px 9px}
  @media(max-width:760px){.review-open .inbox-layout{display:none}}
  .inbox-page{--orange:#d95513;--border:#e8e5e1;--muted:#716d66;--ink:#25231f;background:#fff;color:var(--ink);font-family:Inter,system-ui,sans-serif;height:100dvh;display:flex;flex-direction:column;font-size:14px;text-align:left}
  *{box-sizing:border-box}button,input,select,textarea{font:inherit}button{cursor:pointer;border:1px solid var(--border);background:#fff;color:inherit;border-radius:7px;padding:9px 12px}button:disabled{opacity:.5;cursor:not-allowed}button:hover:not(:disabled){background:#f7f5f1}button:focus-visible,input:focus-visible,select:focus-visible,textarea:focus-visible,a:focus-visible{outline:2px solid var(--orange);outline-offset:2px}input,select,textarea{width:100%;border:1px solid #d5d1cb;border-radius:7px;padding:9px;background:#fff;color:inherit}textarea{resize:vertical;min-height:74px;max-height:180px}label{display:flex;flex-direction:column;gap:5px;font-size:12px;font-weight:600}h1,h2,p{margin:0}h1{font-size:21px}h2{font-size:17px}.topbar{height:64px;min-height:64px;display:flex;align-items:center;gap:26px;padding:0 24px;border-bottom:1px solid var(--border)}.brand{font-size:29px;letter-spacing:-1.5px;color:var(--orange);font-weight:800;text-decoration:none}.brand span{color:#25231f}.workspace{font-size:13px;color:var(--muted)}.staff{margin-left:auto;font-size:12px}.notice{padding:12px 20px;font-size:13px;border-bottom:1px solid var(--border)}.error{background:#fff0ec;color:#972d14}.offline{background:#fff6d7}.inbox-layout{display:grid;grid-template-columns:340px minmax(0,1fr);min-height:0;flex:1}.list-panel{display:flex;flex-direction:column;border-right:1px solid var(--border);min-height:0}.list-title{display:flex;align-items:center;justify-content:space-between;padding:22px 18px 18px;gap:10px}.primary{background:var(--orange);color:#fff;border-color:var(--orange);font-weight:600}.primary:hover:not(:disabled){background:#b7440b}.compact{font-size:11px;padding:8px}.search{margin:0 18px 14px}.filters{display:flex;gap:9px;padding:0 18px 18px;border-bottom:1px solid var(--border)}.filters label{flex:1;min-width:0}.filters select{font-size:12px}.threads{overflow:auto;min-height:0}.thread{border:0;border-bottom:1px solid #f1eeeb;border-radius:0;padding:18px;display:flex;align-items:flex-start;width:100%;gap:11px;text-align:left}.thread.active{background:#fff2e9;box-shadow:inset 3px 0 var(--orange)}.avatar{background:#ecebe6;border-radius:50%;width:37px;height:37px;display:grid;place-items:center;flex-shrink:0;font-size:15px}.thread-summary{display:flex;flex-direction:column;gap:6px;min-width:0;flex:1}.thread-heading{display:flex;gap:8px;justify-content:space-between}.thread-heading strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.thread-summary small{font-size:10px;color:var(--muted)}.excerpt{font-size:12px;color:var(--muted);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.unread{background:var(--orange);color:white;border-radius:12px;min-width:20px;text-align:center;padding:3px 5px;font-size:10px}.conversation-panel{display:flex;flex-direction:column;min-width:0;min-height:0;background:#faf9f7;position:relative}.conversation-header{background:#fff;display:flex;align-items:center;gap:12px;padding:19px 24px;border-bottom:1px solid var(--border)}.conversation-header p{font-size:12px;color:var(--muted);margin-top:5px}.resolve{margin-left:auto;font-size:12px}.back{display:none}.assignment{background:#fff;display:flex;gap:16px;align-items:center;padding:10px 24px;border-bottom:1px solid var(--border);font-size:11px;color:var(--muted)}.assignment label{flex-direction:row;align-items:center;font-size:11px}.assignment select{width:auto;font-size:11px;padding:5px}.timeline{flex:1;overflow:auto;min-height:90px;padding:26px;display:flex;flex-direction:column;gap:17px;scrollbar-gutter:stable}.message{align-self:flex-start;max-width:min(80%,700px);border:1px solid var(--border);border-radius:3px 12px 12px;background:#fff;padding:13px 16px;overflow-wrap:anywhere}.message.outbound{align-self:flex-end;background:#fff0e3;border-color:#f2d9c3;border-radius:12px 3px 12px 12px}.message.note{align-self:stretch;max-width:none;background:#fff8d9;border-color:#eee3aa}.message-meta{font-size:11px;font-weight:650;margin-bottom:7px;color:#716450}.message p{white-space:pre-wrap;line-height:1.6}.message footer{display:flex;justify-content:flex-end;gap:10px;flex-wrap:wrap;margin-top:9px;font-size:10px;color:var(--muted)}.composer{background:#fff;border-top:1px solid var(--border);padding:15px 24px;display:flex;flex-direction:column;gap:12px}.composer.note-mode{background:#fffdf0}.composer-toolbar,.composer-footer{display:flex;justify-content:space-between;align-items:center;gap:12px}.composer-toolbar label{flex-direction:row;align-items:center;font-size:11px}.composer-toolbar select{font-size:12px;width:auto}.composer small{font-size:10px;color:var(--muted)}.policy{font-size:12px;color:#80603c}.message-label{font-size:11px}.template-fields{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}.preview{font-size:13px;white-space:pre-wrap;background:#faf8f4;padding:12px;border-radius:7px;max-height:160px;overflow:auto}.empty{margin:auto;text-align:center;max-width:450px;padding:35px;color:var(--muted)}.empty h2,.empty h1{color:var(--ink);margin:18px 0 10px}.empty p{line-height:1.7;font-size:13px}.empty-icon{font-size:45px;color:var(--orange)}.empty .subtle{font-size:11px;margin-top:13px}.no-results{padding:25px;text-align:center;color:var(--muted);font-size:12px}.load-more{align-self:center;display:block;margin:12px auto;font-size:12px}.new-messages{position:absolute;bottom:220px;align-self:center;background:#fff4e6;border-color:#efc492}.contact-picker{padding:18px;background:#fff8f1;overflow:auto;max-height:300px}.contact-picker h2{margin-bottom:12px}.contact{display:flex;flex-direction:column;gap:5px;text-align:left;width:100%;margin-top:10px}.contact span{font-size:11px;color:var(--muted)}.contact-picker p{margin-top:12px;font-size:12px}
  @media(min-width:1500px){.inbox-layout{grid-template-columns:390px minmax(0,1fr)}.timeline{padding:30px 8%}.composer{padding-left:8%;padding-right:8%}}
  @media(max-width:760px){.topbar{height:54px;min-height:54px;padding:0 14px;gap:15px}.brand{font-size:25px}.workspace{font-size:11px}.staff{max-width:100px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.inbox-layout{display:flex}.list-panel{flex:1;border-right:0}.conversation-panel{display:none}.thread-open .list-panel{display:none}.thread-open .conversation-panel{display:flex;flex:1}.back{display:block}.conversation-header{padding:12px;gap:9px}.conversation-header h2{font-size:15px}.conversation-header p{font-size:10px}.resolve{font-size:10px;padding:7px;max-width:105px}.assignment{padding:8px 12px;gap:8px}.assignment>span{font-size:9px}.timeline{padding:16px 12px}.message{max-width:91%}.composer{padding:12px;padding-bottom:max(12px,env(safe-area-inset-bottom))}.composer-toolbar{align-items:flex-end}.composer-toolbar label{flex-direction:column;align-items:flex-start}.composer-footer small{max-width:180px}.template-fields{grid-template-columns:1fr;max-height:180px;overflow:auto}.template-fields input,.message-label textarea{font-size:16px}}
</style>
